import { DocumentStepsView } from '@guidde/design-system'
import { QuickGuiddeType, StepType, VideoOverlayDataType } from 'app/types'
import { openLink } from 'modules'
import { useEffect, useMemo, useState } from 'react'

const containsRTL = (language: string) => {
    const rtlLanguages = ['ar-DZ', 'iw-IL']
    return rtlLanguages.includes(language)
}

const AMOUNT_OF_STEPS_WITHOUT_LAZY_LOAD = 20

const fallbackData = {
    highlightColor: '#FEC84B',
    font: {
        id: 'Inter, sans-serif',
        url: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
    },
}

type Props = {
    playbook: QuickGuiddeType
    overlayData: VideoOverlayDataType
}

const filterIntroOutroSteps = (steps: Array<StepType>) => {
    return steps.filter(step => !['intro', 'outro'].includes(step.kind))
}

export const DsComponentDocViewWrapper = ({ playbook, overlayData }: Props) => {
    const { steps, language, docSteps, goToLabel } = useMemo(
        () => ({
            ...playbook,
            steps: filterIntroOutroSteps(playbook.steps),
            docSteps: playbook.docSteps ? filterIntroOutroSteps(playbook.docSteps as StepType[]) : undefined,
        }),
        [playbook],
    )

    const [containerWidth, setContainerWidth] = useState(document.body.clientWidth)

    useEffect(() => {
        const handleResize = () => {
            setContainerWidth(document.body.clientWidth)
        }
        window.addEventListener('resize', handleResize)

        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const modifiedSteps = useMemo(() => {
        const getCta = (cta: StepType['cta']) => {
            if (!cta) return undefined

            if (cta.ctaType === 'single') {
                return {
                    ...cta,
                    action: {
                        ...cta.action,
                        isEnabled: Boolean(cta.action.enabled),
                    },
                }
            }

            if (cta.ctaType === 'multiple') {
                return {
                    ...cta,
                    actions: cta.actions.map(action => ({
                        ...action,
                        isEnabled: Boolean(action.enabled),
                    })),
                }
            }
        }

        const targetSteps = docSteps || steps

        return targetSteps.map(step => ({
            ...step,
            cta: getCta(step.cta),
        }))
    }, [docSteps, steps])

    const isRtl = containsRTL(language?.langCode || '')

    const highlightColor = overlayData?.brandKit?.highlightColor ?? fallbackData.highlightColor
    const font = {
        fontFamily: overlayData?.brandKit?.font?.id ?? fallbackData.font.id,
        url: overlayData?.brandKit?.font?.url ?? fallbackData.font.url,
    }

    return (
        <DocumentStepsView
            disableActions={true}
            shouldIgnoreCustomTags={true}
            steps={modifiedSteps as any}
            goToLabel={goToLabel ?? 'Go to'}
            brandKitHighlightColor={highlightColor}
            amountOfStepsWithoutLazyLoad={AMOUNT_OF_STEPS_WITHOUT_LAZY_LOAD}
            isRtl={isRtl}
            containerWidth={containerWidth}
            font={font}
            isStepsGenerating={false}
            onGoToClick={url => openLink(url, '_blank')}
        />
    )
}
